import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';
import { Boxes, LayoutDashboard, ShieldCheck, ShieldAlert, Gauge, ClipboardList, Mail, CalendarDays } from 'lucide-react';
import HelpCenter from '../HelpCenter';
import ModuloHeader from '../ModuloHeader';
import OperacionalAval360 from './aval360/OperacionalAval360';
import OperacionalAutoavaliacao from './autoavaliacao/OperacionalAutoavaliacao';
import OperacionalChecklist from './checklist/OperacionalChecklist';
import OperacionalAvaliacaoSA from './avaliacaosa/OperacionalAvaliacaoSA';
import OperacionalDestinatarios from './destinatarios/OperacionalDestinatarios';
import CalendarioOperacional from './agenda/CalendarioOperacional';
import { perfilNoModulo } from '../../utils/perfisDerivados';

// =====================================================================
// Hub do Módulo Operacional.
// Espelha o padrão dos demais hubs (Manutenção/Treinamento): cards de
// sub-módulos no miolo + recorte por perfil. Paleta ardósia (#475569) —
// escolhida por ser uma faixa neutra ainda não usada pelos outros hubs.
//
// Tela nova do módulo entra no `modulesList` abaixo (id, label, ícone, desc) +
// PERMISSIONS + um `case` no renderContent — igual ao Manutencao.js.
// =====================================================================

const COR = '#475569';

// Quem acessa cada sub-módulo. (Admin sempre tem bypass — ver allowedModules.)
// ⚠️ `Gerente` entrou em 2026-08-26 em LEITURA da própria loja: ele abre a
// consulta, mas os botões "Nova Avaliação"/"Gerenciar versões" são gateados por
// `podePreencher`/`podeGerenciarVersoes` dentro do OperacionalBsv — e o que de
// fato o segura é o backend (PERFIS_PERMITIDOS × PERFIS_LEITURA no server-bsv.js).
const PERMISSIONS = {
  // Avaliação 360 — substitui a BSV. Mesmo recorte: Operacional/Diretoria/Admin
  // preenchem; o Gerente lê a própria loja (gateado no OperacionalAval360 e, de
  // fato, no backend PERFIS_PERMITIDOS × PERFIS_LEITURA do server-aval360.js).
  'aval360': ['Operacional', 'Admin', 'Diretoria', 'Gerente'],
  // ⚠️ AUTOAVALIAÇÃO: o `Gerente` está aqui como quem PREENCHE (é a loja dele
  // avaliando a si mesma) — o inverso do BSV, onde ele só lê. Operacional e
  // Diretoria entram para ACOMPANHAR a rede; quem separa as duas coisas são as
  // listas de perfil do server-autoavaliacao.js, não este mapa (que só decide
  // se o card aparece).
  'autoavaliacao': ['Gerente', 'Operacional', 'Admin', 'Diretoria'],
  // Auditoria Financeira e de Estoques (ex-Checklist do módulo Auditoria) —
  // DEM-00011, Fase 4, T21: mudou de TELA, não de tabela nem de rota de API
  // (`/api/auditoria/checklist` continua o mesmo, ver docs/modulos/auditoria.md).
  // Lista PRESERVADA da que valia lá (`Auditoria.js › PERMISSIONS.forms`):
  // `Auditoria` e `Gerente Adm` são os dois perfis explícitos do pedido de
  // T21; `Diretoria` já alcançava lá e sairia sem tela sem aviso se não
  // viesse junto. Espelha `server-checklist.js › PERFIS_PERMITIDOS` (que não
  // mudou).
  'checklist': ['Auditoria', 'Gerente Adm', 'Diretoria','Operacional'],
  // Avaliação de Segurança do Alimento (ex-Auditoria SA, Treinamento) —
  // DEM-00011, Fase 4, T22: muda de módulo e VOLTA A SER PREENCHIDA — mensal,
  // pelos Consultores de Operações (perfil `Operacional`, reusado). `Treinamento`
  // e `Diretoria` MANTIDOS (decisão tomada na implementação: ninguém perde uma
  // tela que já tinha). Espelha `server-auditoriasa.js › perfis`.
  'avaliacaosa': ['Operacional', 'Treinamento', 'Diretoria'],
  // Destinatários dos avisos — tela de GESTÃO (quem recebe o e-mail com o PDF
  // ao finalizar uma avaliação). Só quem administra a operação: 'Operacional'
  // (+ Admin por bypass). É configuração, não preenchimento de avaliação.
  'destinatarios': ['Operacional'],
  // Calendário de Visitas (DEM-00019, Fase 2) — NÃO é card/submódulo: aparece na
  // Visão Geral, e esta lista só decide QUEM o vê lá. Espelha
  // server-agenda-operacional.js › PERFIS_GESTAO. O recorte por mercado
  // (consultor só as lojas que avalia) é do backend, não deste mapa.
  'agenda': ['Operacional', 'Diretoria', 'Diretoria Op'],
};

// Sub-módulos do hub.
const modulesList = [
  {
    id: 'aval360',
    label: 'Avaliação 360',
    icon: Gauge,
    color: '#475569',
    bgColor: '#f1f5f9',
    desc: 'Disponibiliza uma visão geral do restaurante e, ao mesmo tempo, identificar o desempenho e as oportunidades de melhoria em cada área, com base em indicadores de desempenho e conformidade.',
    stat: 'Formulários',
  },
  {
    id: 'checklist',
    label: 'Auditoria Financeira e de Estoques',
    icon: ClipboardList,
    color: '#475569',
    bgColor: '#f1f5f9',
    desc: 'Checklist de auditoria financeira e de estoques por loja, com nota por área e total.',
    stat: 'Formulários',
  },
  {
    id: 'avaliacaosa',
    label: 'Avaliação de Segurança do Alimento',
    icon: ShieldAlert,
    color: '#475569',
    bgColor: '#f1f5f9',
    desc: 'Auditoria mensal de segurança alimentar por loja, com veto por questão crítica.',
    stat: 'Formulários',
  },
  {
    id: 'autoavaliacao',
    label: 'Autoverificação de Fechamento Mensal',
    icon: ShieldCheck,
    color: '#475569',
    bgColor: '#f1f5f9',
    desc: 'O gerente avalia os controles financeiros e operacionais da própria loja, uma vez por mês.',
    stat: 'Formulários',
  },
  {
    id: 'destinatarios',
    label: 'Destinatários dos avisos',
    icon: Mail,
    color: '#475569',
    bgColor: '#f1f5f9',
    desc: 'Quem recebe o e-mail com o PDF quando uma avaliação é finalizada. Gerente da loja e perfil Operacional já entram automático; aqui se cadastram os extras, por avaliação.',
    stat: 'Configuração',
  },
];

const OperacionalPainel = () => {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : {};
  const userRole = perfilNoModulo(user?.perfil, 'operacional') || '';

  const [isMobile] = useState(window.innerWidth <= 768);
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const allowedModules = modulesList.filter(mod => PERMISSIONS[mod.id]?.includes(userRole) || userRole === 'Admin');

  const OverviewWidgets = () => (
    <div>
      <div style={{
        background: 'linear-gradient(135deg, #64748b 0%, #334155 100%)',
        borderRadius: '16px', padding: '40px', color: '#fff',
        marginBottom: '32px', position: 'relative', overflow: 'hidden',
        boxShadow: '0 10px 15px -3px rgba(71, 85, 105, 0.3)',
      }}>
        <div style={{ position: 'relative', zIndex: 10 }}>
          <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px', marginTop: 0, color: '#fff' }}>
            Operacional
          </h2>
          <p style={{ color: '#e2e8f0', fontSize: '18px', maxWidth: '650px', margin: 0, lineHeight: '1.6' }}>
            Ferramentas da operação do Grupo Correia MC.
          </p>
        </div>
        <div style={{ position: 'absolute', right: '-30px', top: '-40px', opacity: 0.12 }}>
          <Boxes size={250} color="#ffffff" />
        </div>
      </div>

      {allowedModules.length === 0 ? (
        // Estado-vazio: o hub ainda não tem ferramentas (esqueleto).
        <div style={{
          backgroundColor: '#fff', borderRadius: '16px', border: '1px dashed #cbd5e1',
          padding: '48px', textAlign: 'center', color: '#334155',
        }}>
          <Boxes size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 8px 0', color: '#334155' }}>
            Nenhuma ferramenta disponível ainda
          </h3>
          <p style={{ fontSize: '14px', color: '#475569', margin: 0 }}>
            As ferramentas operacionais aparecerão aqui conforme forem adicionadas.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
          {allowedModules.map((mod) => (
            <div
              key={mod.id}
              onClick={() => setActiveTab(mod.id)}
              style={{
                backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
                border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column',
                height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden',
                transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px -2px rgba(0,0,0,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)'; }}
            >
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: mod.color }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: mod.color, marginBottom: '8px', margin: 0 }}>{mod.stat}</p>
                  <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{mod.label}</h3>
                </div>
                <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: mod.bgColor, color: mod.color }}>
                  <mod.icon size={26} />
                </div>
              </div>
              <div style={{ marginTop: 'auto' }}>
                <p style={{ fontSize: '14px', color: '#64748b', fontWeight: '500', lineHeight: '1.5', margin: '0 0 16px 0' }}>{mod.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Calendário de Visitas — vive na Visão Geral (não é card/submódulo).
          Só para quem programa (PERFIS_GESTAO do backend); o recorte por
          mercado é do backend. */}
      {(PERMISSIONS['agenda']?.includes(userRole) || userRole === 'Admin') && (
        <div style={{ marginTop: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <div style={{ padding: '10px', borderRadius: '12px', backgroundColor: '#f1f5f9', color: COR }}>
              <CalendarDays size={22} />
            </div>
            <div>
              <p style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: COR, margin: 0 }}>Programação</p>
              <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: 0 }}>Calendário de Visitas</h3>
            </div>
          </div>
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <CalendarioOperacional />
          </div>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (activeTab !== 'dashboard' && !(PERMISSIONS[activeTab]?.includes(userRole) || userRole === 'Admin')) {
      setActiveTab('dashboard');
    }
  }, [activeTab, userRole]);

  const contentBoxStyle = {
    backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0',
    padding: '32px', minHeight: '75vh', width: '100%',
  };

  const renderContent = () => {
    if (activeTab === 'dashboard') return <OverviewWidgets />;
    switch (activeTab) {
      case 'aval360': return <div style={contentBoxStyle}><OperacionalAval360 /></div>;
      case 'autoavaliacao': return <div style={contentBoxStyle}><OperacionalAutoavaliacao /></div>;
      case 'checklist': return <div style={contentBoxStyle}><OperacionalChecklist /></div>;
      case 'avaliacaosa': return <div style={contentBoxStyle}><OperacionalAvaliacaoSA /></div>;
      case 'destinatarios': return <div style={contentBoxStyle}><OperacionalDestinatarios /></div>;
      default: return <div style={contentBoxStyle} />;
    }
  };

  const getMarginLeft = () => window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px');

  // Por qual verbete o Manual da Tela abre — o da aba ativa (o manual mostra o
  // módulo inteiro; isto só escolhe qual já vem expandido).
  const getSecaoAjuda = () => {
    if (activeTab === 'aval360') return 'aval360';
    if (activeTab === 'autoavaliacao') return 'autoavaliacao';
    if (activeTab === 'checklist') return 'checklist-operacional';
    if (activeTab === 'avaliacaosa') return 'avaliacaosa-operacional';
    return 'visao-geral-operacional';
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f8f8' }}>
      <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
      <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

        <ModuloHeader
          eyebrow="Módulo"
          titulo="Operacional"
          cor={COR}
          onAbrirManual={() => setIsHelpOpen(true)}
          onAbrirMenu={() => setIsCollapsed(false)}
          isMobile={isMobile}
        />

        <div style={{ padding: '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

          {/* BARRA DE NAVEGAÇÃO */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '32px',
            backgroundColor: '#fff', padding: '8px', borderRadius: '12px',
            border: '1px solid #e2e8f0', width: '100%',
          }}>
            <button
              onClick={() => setActiveTab('dashboard')}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                background: activeTab === 'dashboard' ? 'linear-gradient(135deg, #64748b 0%, #475569 100%)' : 'transparent',
                color: activeTab === 'dashboard' ? '#fff' : '#64748b',
                boxShadow: activeTab === 'dashboard' ? '0 4px 6px rgba(71, 85, 105, 0.3)' : 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <LayoutDashboard size={18} /> Visão Geral
            </button>

            {allowedModules.length > 0 && (
              <div style={{ minWidth: '1px', backgroundColor: '#e2e8f0', margin: '0 5px' }} />
            )}

            {allowedModules.map((mod) => (
              <button
                key={mod.id}
                onClick={() => setActiveTab(mod.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 20px', borderRadius: '8px', fontWeight: '600',
                  cursor: 'pointer', transition: 'all 0.2s',
                  border: activeTab === mod.id ? `2px solid ${mod.color}` : '2px solid transparent',
                  backgroundColor: activeTab === mod.id ? mod.bgColor : 'transparent',
                  color: activeTab === mod.id ? mod.color : '#64748b',
                  whiteSpace: 'nowrap',
                }}
              >
                <mod.icon size={18} /> {mod.label}
              </button>
            ))}
          </div>

          {renderContent()}
        </div>
      </div>

      {/* Manual da Tela — verbetes do módulo (HelpCenter › manualOperacional). */}
      <HelpCenter
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        modulo="operacional"
        secaoInicial={getSecaoAjuda()}
      />
    </div>
  );
};

export default OperacionalPainel;
